import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { generateFinancingOptions } from '../../lib/openai';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiDollarSign, FiLoader, FiCopy, FiCheck } = FiIcons;

function FinancingOptions({ vehicle, customer }) {
  const [options, setOptions] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [creditScore, setCreditScore] = useState('Good (670-739)');
  const [copied, setCopied] = useState(false);

  const generateOptions = async () => {
    setIsLoading(true);
    try {
      const customerBudget = customer?.budget || vehicle?.price || 30000;
      const vehiclePrice = vehicle?.price || 30000;
      const result = await generateFinancingOptions(customerBudget, vehiclePrice, creditScore);
      setOptions(result);
    } catch (error) {
      console.error('Error generating financing options:', error);
      setOptions('Failed to generate financing options. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(options);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
        <SafeIcon icon={FiDollarSign} className="w-5 h-5 mr-2 text-primary-500" />
        AI Financing Options
      </h3>
      <div className="space-y-4">
        <div className="bg-gray-50 p-3 rounded-lg mb-4">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <p className="text-gray-500">Vehicle:</p>
            <p className="font-medium">{vehicle ? `${vehicle.year} ${vehicle.make} ${vehicle.model}` : 'Not specified'}</p>
            <p className="text-gray-500">Price:</p>
            <p className="font-medium">{vehicle?.price ? `$${vehicle.price.toLocaleString()}` : 'Not specified'}</p>
            <p className="text-gray-500">Customer Budget:</p>
            <p className="font-medium">{customer?.budget ? `$${customer.budget.toLocaleString()}` : 'Not specified'}</p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Credit Score Range
          </label>
          <select
            value={creditScore}
            onChange={(e) => setCreditScore(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="Excellent (740-850)">Excellent (740-850)</option>
            <option value="Good (670-739)">Good (670-739)</option>
            <option value="Fair (580-669)">Fair (580-669)</option>
            <option value="Poor (300-579)">Poor (300-579)</option>
          </select>
        </div>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={generateOptions}
          disabled={isLoading}
          className="w-full bg-primary-600 text-white py-2 rounded-lg font-medium hover:bg-primary-700 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
        >
          {isLoading ? (
            <>
              <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
              <span>Calculating...</span>
            </>
          ) : (
            <>
              <SafeIcon icon={FiDollarSign} className="w-4 h-4" />
              <span>Generate Financing Options</span>
            </>
          )}
        </motion.button>

        {options && (
          <div className="mt-4 border border-gray-200 rounded-lg p-4 bg-gray-50 relative">
            <pre className="whitespace-pre-wrap text-sm font-sans">{options}</pre>
            <button
              onClick={copyToClipboard}
              className="absolute top-2 right-2 p-2 text-gray-500 hover:text-primary-600 hover:bg-gray-100 rounded-full transition-colors"
              title="Copy to clipboard"
            >
              <SafeIcon
                icon={copied ? FiCheck : FiCopy}
                className={`w-4 h-4 ${copied ? 'text-green-500' : ''}`}
              />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default FinancingOptions;